// timeline.ts — the waveform timeline editor (EGO-132 C.2). A DAW-style view
// over the SAME transcript-edit overlay the list editor uses: dragging a step
// divider, a task cap, or a chunk edge/body just calls the controller's overlay
// mutators (which write transcript_edits.json fields), then re-reads the merged
// view. Zero data logic here — all timing/merge lives in core/src/edits.ts.
//
// Coordinates: everything is seconds-from-audio-anchor. MergedSegment.start/end
// are already in that space; windows convert from ns. Native horizontal scroll
// is the pan; wheel-zoom rescales pxPerSec and re-anchors under the cursor.
import type { MergedSegment, MergedWindow } from "../../core/src/edits.ts";

const stHue = (i: number) => Math.round((i * 137.508) % 360);
const fmt = (s: number) => `${Math.floor(s / 60)}:${(Math.max(0, s) % 60).toFixed(1).padStart(4, "0")}`;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

export interface TimelineData {
  segments: MergedSegment[];
  windows: MergedWindow[];
  duration: number;      // total timeline seconds (may exceed the audio — bad STT times)
  waveDuration: number;  // the AUDIO's own duration — the waveform maps to THIS, not `duration`
  anchor: number;        // ns
}

export interface TimelineCtrl {
  read: () => TimelineData;
  waveform: Float32Array | null;         // 0..1 envelope bins across [0, duration]
  audioEl: () => HTMLAudioElement | null;
  keyFor: (m: MergedSegment) => string;
  selectedKey: () => string | null;
  snap: () => boolean;
  onSelect: (key: string | null) => void;
  editEdge: (key: string, edge: "s" | "e", ns: number) => void;
  moveBody: (key: string, startNs: number) => void;
  moveDivider: (i: number, ns: number) => void;   // shared boundary win i / i+1
  moveCap: (which: "start" | "end", ns: number) => void;
  addAt: (sec: number) => void;
  editText: (key: string, text: string) => void;
  del: (key: string) => void;
}

const esc = (s: unknown) => String(s).replace(/[&<>"']/g, (c) =>
  ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));

export function mountTimeline(host: HTMLElement, ctrl: TimelineCtrl): { update: () => void; destroy: () => void } {
  const d0 = ctrl.read();
  const DUR = Math.max(1, d0.duration);
  const anchor = d0.anchor;
  let pxPerSec = 12;
  let snapOn = ctrl.snap();

  host.innerHTML = `
    <div class="tl-bar">
      <button class="tl-play" data-play>▶</button>
      <span class="tl-clock mono" data-clock>0:00.0</span>
      <span class="tl-now" data-now></span>
      <span class="tl-sp"></span>
      <button class="icon" data-zo title="Zoom out">–</button>
      <button class="icon" data-zi title="Zoom in">+</button>
      <button class="tl-ghost" data-fit>Fit</button>
      <button class="${snapOn ? "on" : ""}" data-snap title="Magnetic snapping (Alt to override)">🧲 Snap</button>
      <button data-add>＋ Add chunk</button>
    </div>
    <div class="tl-hint">space to play/pause · scroll to pan · ⌘/ctrl+scroll to zoom · drag dividers, the gold task caps, and chunk edges/body · hold Alt to override snap · click empty space to deselect</div>
    <div class="tl-stage"><div class="tl-scroll" data-scroll><div class="tl-inner" data-inner>
      <div class="tl-ruler" data-ruler></div>
      <div class="tl-lane-sub" data-sub></div>
      <div class="tl-lane-wave" data-wave><canvas data-canvas></canvas></div>
      <div class="tl-playhead" data-playhead><span class="head"></span></div>
      <div class="tl-snap" data-snapg><span class="pill" data-snappill></span></div>
    </div></div></div>
    <div class="tl-insp" data-insp></div>`;

  const q = <T extends HTMLElement>(sel: string) => host.querySelector<T>(sel)!;
  const scroll = q<HTMLElement>("[data-scroll]"), inner = q<HTMLElement>("[data-inner]");
  const ruler = q<HTMLElement>("[data-ruler]"), laneSub = q<HTMLElement>("[data-sub]");
  const laneWave = q<HTMLElement>("[data-wave]"), canvas = q<HTMLCanvasElement>("[data-canvas]");
  const playheadEl = q<HTMLElement>("[data-playhead]"), snapg = q<HTMLElement>("[data-snapg]");
  const snappill = q<HTMLElement>("[data-snappill]"), inspEl = q<HTMLElement>("[data-insp]");

  const T2X = (t: number) => t * pxPerSec;
  const innerW = () => DUR * pxPerSec;
  const winSec = (w: MergedWindow) => [(w.t_start_ns - anchor) / 1e9, (w.t_end_ns - anchor) / 1e9] as const;
  const secToNs = (sec: number) => Math.round(anchor + sec * 1e9);
  const audio = () => ctrl.audioEl();
  const playheadSec = () => audio()?.currentTime ?? 0;

  // ── waveform (drawn on zoom) ──────────────────────────────────────────────
  // Mapped by the AUDIO's own duration, NOT the timeline width: pixel x is at
  // audio-time x/pxPerSec (the same coordinate as clips + playhead), so the
  // waveform always lines up with what's playing even when a bad STT timestamp
  // stretches the timeline past the end of the audio.
  function drawWave() {
    const W = innerW(), H = laneWave.clientHeight || 150, dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.min(32000, Math.round(W * dpr)); canvas.height = Math.round(H * dpr);
    canvas.style.width = W + "px"; canvas.style.height = H + "px";
    const ctx = canvas.getContext("2d")!; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H);
    const wf = ctrl.waveform, wd = ctrl.read().waveDuration, mid = H / 2;
    if (!wf || !wf.length || wd <= 0) return;
    for (let x = 0; x < W; x++) {
      const at = x / pxPerSec;          // audio-seconds at this pixel
      if (at > wd) break;               // no audio past here
      const bin = Math.min(wf.length - 1, Math.floor((at / wd) * wf.length));
      const a = wf[bin] * (H * 0.44);
      // dark bars on the light lane: stronger for speech, faint for near-silence
      ctx.fillStyle = a > H * 0.05 ? "rgba(74,96,128,.62)" : "rgba(150,160,174,.55)";
      ctx.fillRect(x, mid - a, 1, a * 2);
    }
  }

  // ── position everything from the current merged view ──────────────────────
  function update() {
    const { segments, windows } = ctrl.read();
    const W = innerW();
    inner.style.width = ruler.style.width = laneSub.style.width = laneWave.style.width = W + "px";

    // ruler
    const nice = [1, 2, 5, 10, 15, 30, 60].find((n) => n >= 90 / pxPerSec) || 60;
    ruler.innerHTML = "";
    for (let t = 0; t <= DUR + 0.001; t += nice) {
      const el = document.createElement("div"); el.className = "tl-tick"; el.style.left = T2X(t) + "px";
      el.innerHTML = `<span>${fmt(t).replace(/\.0$/, "")}</span>`; ruler.appendChild(el);
    }

    // sub-task regions + dividers + caps
    laneSub.innerHTML = "";
    windows.forEach((w) => {
      const [a, b] = winSec(w);
      const r = document.createElement("div"); r.className = "tl-region";
      // flush: tiles meet exactly at the boundary (the divider line separates them)
      r.style.cssText = `left:${T2X(a)}px;width:${T2X(b - a)}px;--h:${stHue(w.index)}`;
      r.innerHTML = `<span>${w.index + 1}. ${esc(w.text)}</span>`; laneSub.appendChild(r);
    });
    for (let i = 0; i < windows.length - 1; i++) {
      const div = document.createElement("div"); div.className = "tl-div";
      div.style.left = T2X(winSec(windows[i])[1]) + "px";
      div.innerHTML = '<span class="grip"></span>'; div.onmousedown = (e) => startDivider(e, i, div);
      laneSub.appendChild(div);
    }
    if (windows.length) {
      const caps: [string, number][] = [["start", winSec(windows[0])[0]], ["end", winSec(windows[windows.length - 1])[1]]];
      for (const [which, t] of caps) {
        const cap = document.createElement("div"); cap.className = "tl-cap " + which; cap.style.left = T2X(t) + "px";
        cap.innerHTML = '<span class="grip"></span>'; cap.onmousedown = (e) => startCap(e, which as "start" | "end", cap);
        laneSub.appendChild(cap);
      }
    }

    // chunk clips
    laneWave.querySelectorAll(".tl-clip").forEach((e) => e.remove());
    const selKey = ctrl.selectedKey();
    for (const s of segments) {
      const key = ctrl.keyFor(s);
      const el = document.createElement("div");
      el.className = "tl-clip" + (s.kind === "wake" ? " wake" : "") + (s.deleted ? " del" : "") + (key === selKey ? " sel" : "");
      el.style.cssText = `left:${T2X(s.start)}px;width:${Math.max(6, T2X(s.end - s.start))}px;--h:${stHue(typeof s.subtask === "number" ? s.subtask : 0)}`;
      el.innerHTML = `<div class="h l"></div><div class="ct">${s.kind === "wake" ? "⟳ " : ""}${esc(s.text) || "…"}</div><div class="h r"></div>`;
      el.onmousedown = (e) => startClip(e, s, key, el);
      laneWave.appendChild(el);
    }

    positionPlayhead();
    renderInsp();
    renderNow();
  }

  function positionPlayhead() {
    playheadEl.style.left = T2X(playheadSec()) + "px";
    playheadEl.style.height = (laneSub.offsetHeight + laneWave.offsetHeight) + "px";
  }
  function renderNow() {
    const { windows } = ctrl.read(); const t = playheadSec();
    const w = windows.find((w) => { const [a, b] = winSec(w); return t >= a && t < b; });
    q("[data-now]").innerHTML = w ? `Now: <b>Step ${w.index + 1} · ${esc(w.text)}</b>`
      : (windows.length ? "Now: <b>between steps</b>" : "");
    q("[data-clock]").textContent = fmt(t);
  }

  function renderInsp() {
    const { segments } = ctrl.read();
    const sel = ctrl.selectedKey();
    const s = segments.find((x) => ctrl.keyFor(x) === sel);
    if (!s) { inspEl.className = "tl-insp empty"; inspEl.textContent = "Select a chunk to edit its text, or drag its edges on the timeline."; return; }
    inspEl.className = "tl-insp";
    inspEl.innerHTML = `
      <textarea rows="2">${esc(s.text)}</textarea>
      <div class="tl-imeta">
        <span class="tl-kind">${s.added ? "added" : s.kind ?? "narration"} · Step ${typeof s.subtask === "number" ? s.subtask + 1 : "—"}</span>
        <span class="mono">${fmt(s.start)} → ${fmt(s.end)} · ${(s.end - s.start).toFixed(1)}s</span>
      </div>
      <button class="tl-del">${s.added ? "🗑 Remove" : s.deleted ? "Restore" : "🗑 Delete"}</button>`;
    const ta = inspEl.querySelector("textarea")!;
    ta.oninput = () => { ctrl.editText(sel!, ta.value); const c = laneWave.querySelector(`.tl-clip.sel .ct`); if (c) c.textContent = ta.value || "…"; };
    inspEl.querySelector<HTMLButtonElement>(".tl-del")!.onclick = () => { ctrl.del(sel!); ctrl.onSelect(null); update(); };
  }

  // ── snapping ──────────────────────────────────────────────────────────────
  function snapTargets(excludeKey: string | null): number[] {
    const { segments, windows } = ctrl.read();
    const t = [playheadSec()];
    for (const w of windows) { const [a, b] = winSec(w); t.push(a, b); }
    for (const s of segments) { if (ctrl.keyFor(s) === excludeKey) continue; t.push(s.start, s.end); }
    return t;
  }
  function doSnap(sec: number, excludeKey: string | null, alt: boolean): number {
    if (!snapOn || alt) { hideSnap(); return sec; }
    const thr = 7 / pxPerSec; let best: number | null = null, bd = thr;
    for (const c of snapTargets(excludeKey)) { const dd = Math.abs(c - sec); if (dd < bd) { bd = dd; best = c; } }
    if (best != null) { showSnap(best); return best; } hideSnap(); return sec;
  }
  function showSnap(sec: number) {
    snapg.classList.add("on"); snapg.style.left = T2X(sec) + "px";
    snapg.style.height = (ruler.offsetHeight + laneSub.offsetHeight + laneWave.offsetHeight) + "px";
    snappill.textContent = fmt(sec);
  }
  function hideSnap() { snapg.classList.remove("on"); }

  // ── drag machinery ────────────────────────────────────────────────────────
  const innerX = (e: MouseEvent) => e.clientX - inner.getBoundingClientRect().left;
  function drag(onMove: (e: MouseEvent) => void, onUp?: () => void) {
    const mm = (e: MouseEvent) => onMove(e);
    const mu = () => { document.removeEventListener("mousemove", mm); document.removeEventListener("mouseup", mu); hideSnap(); onUp?.(); };
    document.addEventListener("mousemove", mm); document.addEventListener("mouseup", mu);
  }

  function startDivider(e: MouseEvent, i: number, el: HTMLElement) {
    e.preventDefault(); e.stopPropagation(); el.classList.add("drag");
    drag((ev) => {
      const { windows } = ctrl.read();
      const lo = winSec(windows[i])[0] + 0.2, hi = winSec(windows[i + 1])[1] - 0.2;
      let t = clamp(innerX(ev) / pxPerSec, lo, hi);
      t = clamp(doSnap(t, null, ev.altKey), lo, hi);
      ctrl.moveDivider(i, secToNs(t)); update();
    }, () => el.classList.remove("drag"));
  }
  function startCap(e: MouseEvent, which: "start" | "end", el: HTMLElement) {
    e.preventDefault(); e.stopPropagation(); el.classList.add("drag");
    drag((ev) => {
      const { windows } = ctrl.read();
      let t = doSnap(innerX(ev) / pxPerSec, null, ev.altKey);
      if (which === "start") t = clamp(t, 0, winSec(windows[0])[1] - 0.2);
      else t = clamp(t, winSec(windows[windows.length - 1])[0] + 0.2, DUR);
      ctrl.moveCap(which, secToNs(t)); update();
    }, () => el.classList.remove("drag"));
  }
  function startClip(e: MouseEvent, s: MergedSegment, key: string, el: HTMLElement) {
    e.preventDefault();
    const tgt = e.target as HTMLElement;
    const edge = tgt.classList.contains("h") ? (tgt.classList.contains("l") ? "l" : "r") : "body";
    ctrl.onSelect(key); update();
    if (edge === "body") {
      const cur = ctrl.read().segments.find((x) => ctrl.keyFor(x) === key)!;
      const grab = innerX(e) / pxPerSec, len = cur.end - cur.start, s0 = cur.start;
      el.style.cursor = "grabbing";
      drag((ev) => {
        let ns = s0 + (innerX(ev) / pxPerSec - grab);
        ns = clamp(doSnap(ns, key, ev.altKey), 0, DUR - len);
        ctrl.moveBody(key, secToNs(ns)); update();
      });
    } else if (edge === "l") {
      drag((ev) => {
        const cur = ctrl.read().segments.find((x) => ctrl.keyFor(x) === key)!;
        const t = clamp(doSnap(innerX(ev) / pxPerSec, key, ev.altKey), 0, cur.end - 0.2);
        ctrl.editEdge(key, "s", secToNs(t)); update();
      });
    } else {
      drag((ev) => {
        const cur = ctrl.read().segments.find((x) => ctrl.keyFor(x) === key)!;
        const t = clamp(doSnap(innerX(ev) / pxPerSec, key, ev.altKey), cur.start + 0.2, DUR);
        ctrl.editEdge(key, "e", secToNs(t)); update();
      });
    }
  }

  // ── ruler scrub ───────────────────────────────────────────────────────────
  ruler.addEventListener("mousedown", (e) => {
    e.preventDefault();
    const move = (ev: MouseEvent) => { const a = audio(); if (a) { a.currentTime = clamp(innerX(ev) / pxPerSec, 0, DUR); positionPlayhead(); renderNow(); } };
    move(e); drag(move);
  });

  // Clicking empty track area (not a chunk) clears the selection → the inspector
  // empties. Clips bubble here but are excluded; dividers/caps stopPropagation
  // in their own handlers, so a boundary drag never deselects.
  const clearIfEmpty = (e: MouseEvent) => {
    if ((e.target as HTMLElement).closest(".tl-clip")) return;
    if (ctrl.selectedKey() == null) return;
    ctrl.onSelect(null); update();
  };
  laneWave.addEventListener("mousedown", clearIfEmpty);
  laneSub.addEventListener("mousedown", clearIfEmpty);

  // ── zoom ──────────────────────────────────────────────────────────────────
  function setZoom(next: number, anchorClientX?: number) {
    const rect = scroll.getBoundingClientRect();
    const ax = (anchorClientX ?? rect.left + rect.width / 2) - rect.left;
    const tAt = (scroll.scrollLeft + ax) / pxPerSec;
    pxPerSec = clamp(next, 4, 160);
    drawWave(); update();
    scroll.scrollLeft = tAt * pxPerSec - ax;
  }
  const onWheel = (e: WheelEvent) => { if (e.ctrlKey || e.metaKey) { e.preventDefault(); setZoom(pxPerSec * (e.deltaY < 0 ? 1.12 : 0.89), e.clientX); } };
  scroll.addEventListener("wheel", onWheel, { passive: false });
  q<HTMLButtonElement>("[data-zi]").onclick = () => setZoom(pxPerSec * 1.3);
  q<HTMLButtonElement>("[data-zo]").onclick = () => setZoom(pxPerSec / 1.3);
  q<HTMLButtonElement>("[data-fit]").onclick = () => { setZoom((scroll.clientWidth - 4) / DUR); scroll.scrollLeft = 0; };

  // ── transport + toolbar ─────────────────────────────────────────────────
  const playBtn = q<HTMLButtonElement>("[data-play]");
  playBtn.onclick = () => { const a = audio(); if (!a) return; a.paused ? void a.play() : a.pause(); };
  const onTU = () => { positionPlayhead(); renderNow(); };
  const bindAudio = () => { const a = audio(); if (!a) return;
    a.addEventListener("timeupdate", onTU);
    a.addEventListener("play", () => { playBtn.textContent = "⏸"; });
    a.addEventListener("pause", () => { playBtn.textContent = "▶"; });
  };
  bindAudio();
  q<HTMLButtonElement>("[data-snap]").onclick = (e) => { snapOn = !snapOn; (e.currentTarget as HTMLElement).classList.toggle("on", snapOn); };
  q<HTMLButtonElement>("[data-add]").onclick = () => { ctrl.addAt(clamp(playheadSec(), 0, DUR - 2)); update(); setTimeout(() => inspEl.querySelector("textarea")?.focus(), 0); };

  // boot: comfortable zoom anchored at start
  drawWave(); update();
  requestAnimationFrame(() => { pxPerSec = Math.max(9, (scroll.clientWidth - 4) / DUR * 1.5); drawWave(); update(); scroll.scrollLeft = 0; });

  return {
    update,
    destroy() { scroll.removeEventListener("wheel", onWheel); const a = audio(); a?.removeEventListener("timeupdate", onTU); host.innerHTML = ""; },
  };
}
